import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Footer from "../../components/footer";
import Header from "../../components/header";
import Loader from "../../components/loader";
import ReportWrapper from "../../components/report-wrapper";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";
import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import { IQ_TEST_ASSESSMENT_ID, MODEL_TYPE_IQ, IQ_CONTENT_KEY, cdnUrl, cdnSign } from "../../utils/general-config";
import PathHelper from "../../utils/path-helper";
import { mapIQReportModel } from "./report-model";
import { MOCK_ASSESSMENT_RESULT } from "./mock-data";
import { getDomainVisualStyle, getPsychometricBandVisualStyle } from "./report-visual-style";
import { PSYCHOMETRIC_CLASSIFICATION_BANDS } from "./iq-psychometric-config";
import ReportDebugPanel, {
  EMPTY_DEV_OVERRIDES,
  NAMED_DEV_PRESETS,
  applyDevSourceInputs,
  isDevPreviewAllowed,
  isNonProdEnv,
} from "./dev-tools/report-debug-panel";

/* ============================================================
   PRIMITIVES (typography, bars, cards)
   ============================================================ */

// Display-only rounding for percentage TEXT (never for a bar's `width`
// style, which keeps full precision) — `reportData` itself always keeps
// full calculated precision.
const formatPercentageDisplay = (percentage) => (percentage == null ? percentage : Math.round(percentage));

function Eyebrow({ children }) {
  return (
    <p className="text-sm font-semibold tracking-widest text-orange-400 uppercase mb-3">
      {children}
    </p>
  );
}

// Report-header-only kicker (font-size 0.8rem/weight 700/tracking
// 0.12em/coral-ink) — kept separate from Eyebrow so other sections
// reusing Eyebrow are unaffected.
function HeroKicker({ children }) {
  return (
    <p className="text-[0.8rem] font-bold tracking-[0.12em] text-[#F2744F] uppercase mb-[10px]">
      {children}
    </p>
  );
}

// `size`: "md" (CardHeader's "Where you rank") or the default "lg" (the
// report's own H1).
function Title({ children, size = "lg" }) {
  const sizes = {
    md: "text-4xl md:text-5xl",
    lg: "text-5xl md:text-6xl",
  };
  // Editorial wrap for the report's top hero title only (designer reference's
  // h1 uses a max-width:18ch so the headline wraps intentionally instead of
  // running ragged wide) — "md"-sized titles (e.g. "Where you rank") are
  // short phrases well under 18ch, so the constraint would be a no-op there
  // anyway. Tighter tracking, by contrast, is a display-type treatment both
  // sizes benefit from equally, so it's unconditional below.
  const heroOnly = size === "lg" ? "max-w-[18ch]" : "";

  return (
    <h1 className={`${sizes[size]} font-extrabold text-neutral-900 leading-[1.05] tracking-tight ${heroOnly} mb-6`}>
      {children}
    </h1>
  );
}

function Subtitle({ children }) {
  // Warm muted tone + narrower measure to match the designer reference's
  // ".lede" (color var(--muted) #6E635B, max-width 62ch) instead of a cool
  // neutral gray at desktop-paragraph width. Sized down a step on mobile
  // (18px) rather than flat 20px everywhere — matches the more conservative
  // mobile body-text scale already established by CtaDescription, since 20px
  // is otherwise the single largest plain paragraph on a narrow screen.
  return <p className="text-lg sm:text-xl text-[#6E635B] leading-relaxed max-w-[62ch] mb-10">{children}</p>;
}

function Description({ children, className = "" }) {
  return <p className={`text-base text-neutral-500 leading-relaxed text-left ${className}`}>{children}</p>;
}

function ScoreBar({ label, percentage, barColor = "bg-sky-300", classificationLabel }) {
  return (
    <div className="mb-6">
      <div className="flex items-baseline justify-between mb-3">
        <span className="text-sm font-bold tracking-widest text-neutral-400 uppercase">{label}</span>
        <span className="text-xl font-extrabold text-neutral-900">{classificationLabel}</span>
      </div>
      <div className="h-3 w-full rounded-full bg-neutral-200">
        <div className={`h-3 rounded-full ${barColor}`} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
}

function InsightCard({ tone, label, children }) {
  const tones = {
    strength: { bg: "bg-emerald-50", dot: "bg-emerald-600", label: "text-emerald-800" },
    improve: { bg: "bg-amber-50", dot: "bg-amber-500", label: "text-amber-800" },
  };
  const t = tones[tone];

  return (
    <div className={`flex-1 rounded-2xl ${t.bg} p-6`}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-2 h-2 rounded-full ${t.dot}`} />
        <span className={`font-bold ${t.label}`}>{label}</span>
      </div>
      <p className="text-neutral-600 leading-relaxed text-left">{children}</p>
    </div>
  );
}

function InsightRow({ strength, improve }) {
  if (!strength && !improve) return null;
  // Side-by-side only once its column is genuinely wide enough to read
  // comfortably (measured ~230px+ per card) — that's min-[1152px], the
  // same point DeepDiveSection's own text column reaches full width.
  // Between md (821px) and 1152px the text column is narrower (sharing
  // the row with the illustration — see DeepDiveSection), so this stays
  // stacked there rather than compounding into cramped cards.
  return (
    <div className="flex flex-col min-[1152px]:flex-row gap-4">
      {strength && (
        <InsightCard tone="strength" label="Strength">
          {strength}
        </InsightCard>
      )}
      {improve && (
        <InsightCard tone="improve" label="Where to improve">
          {improve}
        </InsightCard>
      )}
    </div>
  );
}

/* ============================================================
   HERO: SCORE CARD + DOMAIN SCORES CARD
   ============================================================ */

// `compact` is keyed off TILE COUNT, not a viewport breakpoint: this card
// shares a row with DomainScoresCard, so its own width stays ~260-450px
// Design-parity experiment (pk/experiment/iq-report-design-parity): tile
// styling reproduces the reference HTML literally — flex:1 1 90px, no
// viewport-driven "compact" mode. If 3 tiles don't fit a row, the design's
// own flex-wrap lets a tile wrap to a new line; that is the design's
// actual behavior, not a bug to engineer around in this experiment.
function StatBox({ value, suffix, label }) {
  return (
    <div style={{ flex: "1 1 90px", background: "rgba(255,255,255,0.08)", borderRadius: "14px", padding: "14px" }}>
      <div style={{ fontFamily: "Sora, sans-serif", fontWeight: 800, fontSize: "1.5rem" }}>
        {value}
        {suffix && <span style={{ opacity: 0.55, fontSize: "1rem", fontWeight: 600 }}>{suffix}</span>}
      </div>
      <div
        style={{
          fontSize: "0.74rem",
          opacity: 0.7,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          marginTop: "2px",
        }}
      >
        {label}
      </div>
    </div>
  );
}

// Hero primary metric — headline number/badge come from
// `overallResult.psychometric` (the fixed Mean=40.5/SD=7.8 IQ/deviation
// index), NOT `overallResult.percentage`/`classificationLabel` (the
// separate confirmed PERFORMANCE system, shown as the lede above this
// card and throughout Section Scores/Deep Dive instead — never
// duplicated inside this badge). confidenceInterval/reliability/SEM are
// absent: no confirmed methodology exists for them under this model.
function IQCard({ overallResult }) {
  const { rawScore, maxRawScore, percentage, psychometric, consistency } = overallResult;
  const estimatedIq = psychometric?.estimatedIq;
  const classification = psychometric?.classification; // {key,label} — 7-band deviation classification, distinct from overallResult.classificationLabel
  const percentile = psychometric?.percentile;
  const bandStyle = getPsychometricBandVisualStyle(classification?.key);
  const hasCorrect = rawScore != null && maxRawScore != null;
  const hasConsistency = consistency != null;

  return (
    <div
      className="relative overflow-hidden"
      style={{
        flex: "1 1 300px",
        background: "rgb(43, 36, 32)",
        color: "rgb(255, 247, 242)",
        borderRadius: "24px",
        padding: "clamp(26px, 3vw, 38px)",
        boxShadow: "rgba(43, 36, 32, 0.5) 0px 22px 40px -22px",
      }}
    >
      <div
        className="absolute rounded-full"
        style={{ top: "-50px", right: "-40px", width: "200px", height: "200px", background: "rgba(255, 140, 107, 0.22)" }}
      />

      <div className="relative">
        <div style={{ fontSize: "0.8rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.65 }}>
          Estimated IQ
        </div>

        <div className="flex items-baseline" style={{ gap: "14px", marginTop: "6px" }}>
          <div
            className="font-extrabold leading-[0.9]"
            style={{ fontFamily: "Sora, sans-serif", fontSize: "clamp(3.4rem, 8vw, 5rem)", letterSpacing: "-0.04em" }}
          >
            {estimatedIq}
          </div>
          {classification?.label && (
            <span
              className={`inline-block ${bandStyle.badgeBg} ${bandStyle.badgeText}`}
              style={{
                fontFamily: "Sora, sans-serif",
                fontWeight: 800,
                fontSize: "0.82rem",
                padding: "6px 13px",
                borderRadius: "999px",
              }}
            >
              {classification.label}
            </span>
          )}
        </div>

        {percentile != null && (
          <div className="mt-[18px] text-[0.98rem] opacity-85">
            {/* formatPercentileSentence is shared with the Bell Curve marker below. */}
            {formatPercentileSentence(percentile)}
          </div>
        )}
        {/* classificationDescription (the percentage-band narrative)
            renders once, as the report's top lede above this card —
            never duplicated here. */}

        <div className="flex flex-wrap" style={{ gap: "10px", marginTop: "22px" }}>
          {hasCorrect && <StatBox value={rawScore} suffix={`/${maxRawScore}`} label="Correct" />}
          <StatBox value={`${formatPercentageDisplay(percentage)}%`} label="Accuracy" />
          {hasConsistency && <StatBox value={`${formatPercentageDisplay(consistency)}%`} label="Consistency" />}
        </div>
      </div>
    </div>
  );
}

// Stacked below `lg` (1024px — the same fixed viewport breakpoint at
// every card width, not intrinsic content-wrap): name on its own line,
// band · percentage below it. At `lg` and up, name and band · percentage
// share one row (name left, label right) — verified by direct measurement
// that the Section Scores card is wide enough there for the longest real
// combo ("Pattern Recognition" / "Slightly Above Average · 80%") with no
// truncation or overlap. The `gap` shorthand (row-gap column-gap) works
// correctly in both the stacked and row orientations without a separate
// override. Progress bar always stays on its own line below, unaffected.
function DomainScoreItem({ domain }) {
  const style = getDomainVisualStyle(domain.key);
  return (
    <div>
      <div
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between"
        style={{ gap: "2px 12px", marginBottom: "8px" }}
      >
        <div className="flex items-center" style={{ gap: "9px" }}>
          <span className={`w-3 h-3 rounded-full flex-shrink-0 ${style.dot}`} />
          <span style={{ fontFamily: "Sora, sans-serif", fontWeight: 600, fontSize: "0.92rem" }}>
            {domain.title || domain.key}
          </span>
        </div>
        <div style={{ fontSize: "0.84rem", color: "#6E635B", fontWeight: 700 }}>
          {domain.classificationLabel ? `${domain.classificationLabel} · ` : ""}
          {formatPercentageDisplay(domain.percentage)}%
        </div>
      </div>
      <div className="w-full rounded-full bg-[rgba(43,36,32,0.06)] overflow-hidden" style={{ height: "12px" }}>
        <div className={`h-full rounded-full ${style.dot}`} style={{ width: `${domain.percentage}%` }} />
      </div>
    </div>
  );
}

function DomainScoresCard({ domains }) {
  return (
    <div
      className="bg-white"
      style={{
        flex: "1 1 380px",
        border: "1px solid rgba(43,36,32,0.06)",
        borderRadius: "24px",
        padding: "clamp(24px, 3vw, 34px)",
        boxShadow: "0 14px 36px -18px rgba(43,36,32,0.35)",
      }}
    >
      <div className="flex" style={{ alignItems: "baseline", justifyContent: "space-between", gap: "12px" }}>
        <h2 style={{ fontFamily: "Sora, sans-serif", fontWeight: 700, letterSpacing: "-0.02em", fontSize: "1.2rem", margin: 0 }}>
          Section scores
        </h2>
        <span style={{ fontSize: "0.78rem", color: "#8A7F76", fontWeight: 600 }}>{domains.length} sections</span>
      </div>
      <div className="flex flex-col" style={{ gap: "18px", marginTop: "22px" }}>
        {domains.map((domain) => (
          <DomainScoreItem key={domain.key} domain={domain} />
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   "WHERE YOU RANK" BELL CURVE CARD
   ============================================================ */

function CardHeader({ eyebrow, title, description }) {
  return (
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-6">
      <div>
        <Eyebrow>{eyebrow}</Eyebrow>
        <Title size="md">{title}</Title>
      </div>
      {description && <Description className="max-w-md md:text-right">{description}</Description>}
    </div>
  );
}

function LegendItem({ children, kind }) {
  return (
    <div className="flex items-center gap-2 text-neutral-600 text-base">
      {kind === "fill" && <span className="w-4 h-4 rounded-sm bg-[#F5C4B3]" />}
      {kind === "line" && <span className="w-5 h-0.5 bg-orange-400 inline-block" />}
      {kind === "ci" && (
        <span className="w-4 h-4 rounded-sm" style={{ background: "rgba(255,140,107,0.22)" }} />
      )}
      {kind === "dashed" && (
        <span
          className="w-5 h-0.5 inline-block"
          style={{ backgroundImage: "repeating-linear-gradient(to right, #9CA3AF 0 4px, transparent 4px 8px)" }}
        />
      )}
      <span>{children}</span>
    </div>
  );
}

function Legend({ confidenceInterval }) {
  const hasCi = confidenceInterval?.low != null && confidenceInterval?.high != null;
  return (
    <div className="flex flex-wrap items-center gap-6 mt-4">
      <LegendItem kind="fill">Scores below you</LegendItem>
      <LegendItem kind="line">Your score</LegendItem>
      {/* Only shown when the source actually supplies a CI (DEV FIXTURE
          scenarios today) — never a fabricated placeholder. */}
      {hasCi && (
        <LegendItem kind="ci">
          95% interval ({confidenceInterval.low}–{confidenceInterval.high})
        </LegendItem>
      )}
      <LegendItem kind="dashed">Population average (100)</LegendItem>
    </div>
  );
}

const BELL_CURVE_FILL =
  "M70.0,280 L70.0,277.7 L78.4,277.2 L86.9,276.6 L95.3,275.8 L103.8,275.0 L112.2,274.0 L120.7,272.9 L129.1,271.5 L137.6,270.0 L146.0,268.2 L154.4,266.2 L162.9,263.9 L171.3,261.3 L179.8,258.4 L188.2,255.2 L196.7,251.6 L205.1,247.6 L213.6,243.2 L222.0,238.4 L230.4,233.2 L238.9,227.6 L247.3,221.6 L255.8,215.2 L264.2,208.4 L272.7,201.2 L281.1,193.7 L289.6,185.9 L298.0,177.8 L306.4,169.5 L314.9,161.1 L323.3,152.6 L331.8,144.1 L340.2,135.7 L348.7,127.5 L357.1,119.5 L365.6,111.8 L374.0,104.6 L382.4,97.8 L390.9,91.7 L399.3,86.1 L407.8,81.3 L416.2,77.3 L424.7,74.2 L433.1,71.9 L441.6,70.5 L450.0,70.0 L458.4,70.5 L466.9,71.9 L475.3,74.2 L483.8,77.3 L492.2,81.3 L500.7,86.1 L509.1,91.7 L517.6,97.8 L526.0,104.6 L534.4,111.8 L542.9,119.5 L551.3,127.5 L559.8,135.7 L568.2,144.1 L576.7,152.6 L585.1,161.1 L593.6,169.5 L602.0,177.8 L610.4,185.9 L618.9,193.7 L627.3,201.2 L635.8,208.4 L644.2,215.2 L652.7,221.6 L661.1,227.6 L669.6,233.2 L678.0,238.4 L686.4,243.2 L694.9,247.6 L703.3,251.6 L711.8,255.2 L720.2,258.4 L728.7,261.3 L737.1,263.9 L745.6,266.2 L754.0,268.2 L762.4,270.0 L770.9,271.5 L779.3,272.9 L787.8,274.0 L796.2,275.0 L804.7,275.8 L813.1,276.6 L821.6,277.2 L830.0,277.7 L830.0,280 Z";

const BELL_CURVE_OUTLINE =
  "M70.0,277.7 L78.4,277.2 L86.9,276.6 L95.3,275.8 L103.8,275.0 L112.2,274.0 L120.7,272.9 L129.1,271.5 L137.6,270.0 L146.0,268.2 L154.4,266.2 L162.9,263.9 L171.3,261.3 L179.8,258.4 L188.2,255.2 L196.7,251.6 L205.1,247.6 L213.6,243.2 L222.0,238.4 L230.4,233.2 L238.9,227.6 L247.3,221.6 L255.8,215.2 L264.2,208.4 L272.7,201.2 L281.1,193.7 L289.6,185.9 L298.0,177.8 L306.4,169.5 L314.9,161.1 L323.3,152.6 L331.8,144.1 L340.2,135.7 L348.7,127.5 L357.1,119.5 L365.6,111.8 L374.0,104.6 L382.4,97.8 L390.9,91.7 L399.3,86.1 L407.8,81.3 L416.2,77.3 L424.7,74.2 L433.1,71.9 L441.6,70.5 L450.0,70.0 L458.4,70.5 L466.9,71.9 L475.3,74.2 L483.8,77.3 L492.2,81.3 L500.7,86.1 L509.1,91.7 L517.6,97.8 L526.0,104.6 L534.4,111.8 L542.9,119.5 L551.3,127.5 L559.8,135.7 L568.2,144.1 L576.7,152.6 L585.1,161.1 L593.6,169.5 L602.0,177.8 L610.4,185.9 L618.9,193.7 L627.3,201.2 L635.8,208.4 L644.2,215.2 L652.7,221.6 L661.1,227.6 L669.6,233.2 L678.0,238.4 L686.4,243.2 L694.9,247.6 L703.3,251.6 L711.8,255.2 L720.2,258.4 L728.7,261.3 L737.1,263.9 L745.6,266.2 L754.0,268.2 L762.4,270.0 L770.9,271.5 L779.3,272.9 L787.8,274.0 L796.2,275.0 L804.7,275.8 L813.1,276.6 L821.6,277.2 L830.0,277.7";

const BELL_CURVE_TICKS = [
  { x: 70.0, label: "55" },
  { x: 196.7, label: "70" },
  { x: 323.3, label: "85" },
  { x: 450.0, label: "100" },
  { x: 576.7, label: "115" },
  { x: 703.3, label: "130" },
  { x: 830.0, label: "145" },
];

// Pure rendering geometry for marker placement on a fixed, hand-drawn
// decorative curve — NOT a scoring computation. `index` is an already-
// supplied display value (mock now, real later); this only decides where
// its dot lands on the chart's M=100/SD=15 axis.
const xForIndex = (index) => Math.min(830, Math.max(70, 450 + (index - 100) * 8.4444));
const yForCurveX = (x) => 280 - 210 * Math.exp(-0.5 * ((x - 450) / 145) ** 2);

// Pure text-formatting utility (1st/2nd/3rd/4th…) — not a scoring or
// percentile computation, `n` is an already-supplied display value.
const ordinalSuffix = (n) => {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return "th";
  switch (n % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

// A rounded percentile of 0 (e.g. an unrounded percentile of 0.43 for a
// very low raw score) is mathematically correct, but rendering it as "0th
// percentile — ahead of 0% of test-takers" reads as "you outrank nobody,"
// which is misleading phrasing rather than a math error. Below the 1st
// percentile, say so plainly instead. `percentile` here is always the
// already-rounded integer from computeFixedNormIq — this is presentation
// only, no percentile math lives here.
const isBelowFirstPercentile = (percentile) => percentile === 0;

// Shared by the IQCard sentence below and the Bell Curve marker label
// further down so the two never diverge in wording for the same result.
export const formatPercentileSentence = (percentile) =>
  isBelowFirstPercentile(percentile)
    ? "You scored below the 1st percentile."
    : `You scored in the ${percentile}${ordinalSuffix(percentile)} percentile — ahead of ${percentile}% of test-takers.`;

export const formatPercentileMarkerLabel = (percentile) =>
  isBelowFirstPercentile(percentile) ? "<1st" : `${percentile}${ordinalSuffix(percentile)}`;

function BellCurveChart({ index, percentile, confidenceInterval }) {
  const markerX = xForIndex(index);
  const markerY = yForCurveX(markerX);
  const hasCi = confidenceInterval?.low != null && confidenceInterval?.high != null;
  const ciX1 = hasCi ? xForIndex(confidenceInterval.low) : null;
  const ciX2 = hasCi ? xForIndex(confidenceInterval.high) : null;

  // Two chart sizes share the same 900-unit-wide x-coordinate system, so
  // the curve shape, tick x-positions, and the marker's x-position (via
  // xForIndex) are identical between them — only the viewBox height and
  // "chrome" (font-size/stroke-width/marker-radius/label-box) unit values
  // differ. This is a real geometry fix, not a cosmetic one: an SVG's
  // rendered pixel size for any user-unit value is fixed by
  // renderedWidth/viewBoxWidth alone, and at mobile widths that ratio
  // shrinks enough that the desktop chrome values become unreadably small
  // (measured ~5px tick-label text at a 344px viewport) — while
  // viewBoxWidth can't be reduced far without clipping the existing curve/
  // tick coordinates. A plain CSS `transform: scale()` was avoided in
  // favor of real geometry, per this ticket's constraint. DESKTOP mirrors
  // the pre-existing numbers exactly (unchanged appearance); MOBILE uses a
  // taller viewBox and larger chrome units so the same fixed-width curve
  // data renders with legible labels/marker on narrow screens.
  const CHART_SIZES = {
    mobile: {
      viewBoxHeight: 420,
      // Shifts the whole plot (curve, axis, marker, labels) down as one
      // unit inside the taller viewBox via a <g transform="translate">
      // (a position shift, not a scale) — this is what actually creates
      // the extra headroom the bigger mobile label box/text need above
      // the curve peak, without moving any curve/tick coordinate.
      translateY: 60,
      baselineStrokeWidth: 3,
      curveStrokeWidth: 3.5,
      tickLineY2: 294,
      tickFontSize: 34,
      tickTextY: 336,
      avgStrokeWidth: 3,
      avgFontSize: 30,
      avgTextY: 46,
      markerRadius: 13,
      markerCircleStrokeWidth: 4,
      markerLineStrokeWidth: 5,
      // Box/pointer position is derived from these three values (see
      // renderChart) rather than a hand-picked y-offset, so the callout
      // is guaranteed to sit a small, fixed gap above the marker circle —
      // however big the box/font get — instead of drifting apart the way
      // a flat offset constant could.
      pointerGap: 4,
      pointerHeight: 16,
      pointerBaseHalfWidth: 12,
      labelBoxWidth: 344,
      labelBoxHeight: 56,
      labelBoxRx: 28,
      labelFontSize: 40,
    },
    desktop: {
      viewBoxHeight: 320,
      translateY: 0,
      baselineStrokeWidth: 2,
      curveStrokeWidth: 2.5,
      tickLineY2: 286,
      tickFontSize: 15,
      tickTextY: 306,
      avgStrokeWidth: 2,
      avgFontSize: 15,
      avgTextY: 58,
      markerRadius: 7,
      markerCircleStrokeWidth: 3,
      markerLineStrokeWidth: 3,
      // Reproduces the pre-existing box position exactly (markerY-52 top /
      // markerY-18 bottom) via the same derivation mobile uses — the
      // pointer just fills the gap that was already there, unchanged.
      pointerGap: 3,
      pointerHeight: 8,
      pointerBaseHalfWidth: 7,
      labelBoxWidth: 196,
      labelBoxHeight: 34,
      labelBoxRx: 17,
      labelFontSize: 15,
    },
  };

  const renderChart = (variant, size, className) => {
    // Derives the callout box/pointer from the marker's own position and a
    // small fixed gap, instead of a flat y-offset constant — this is what
    // keeps the box visually attached to the marker (pointer tip a few
    // units above the circle) regardless of box/font size, and is shared
    // by both variants so any future size tweak can't reintroduce a
    // drifted-apart callout.
    const pointerTipY = markerY - size.markerRadius - size.pointerGap;
    const pointerBaseY = pointerTipY - size.pointerHeight;
    const boxBottomY = pointerBaseY;
    const boxTopY = boxBottomY - size.labelBoxHeight;
    const boxHalfWidth = size.labelBoxWidth / 2;
    const edgeMargin = 6;
    const minBoxX = edgeMargin;
    const maxBoxX = 900 - size.labelBoxWidth - edgeMargin;
    const boxX = Math.min(maxBoxX, Math.max(minBoxX, markerX - boxHalfWidth));
    // The pointer always targets the real markerX, clamped only enough to
    // stay attached to the (possibly edge-clamped) box — so a callout
    // pushed off-center near the score extremes still visibly points at
    // the true marker instead of snapping to the box's own center.
    const pointerInset = size.labelBoxRx * 0.6 + size.pointerBaseHalfWidth;
    const pointerX = Math.min(
      boxX + size.labelBoxWidth - pointerInset,
      Math.max(boxX + pointerInset, markerX)
    );
    const textY = boxTopY + size.labelBoxHeight / 2 + size.labelFontSize * 0.35;

    return (
    <svg
      className={className}
      viewBox={`0 0 900 ${size.viewBoxHeight}`}
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* Clips the (already-static) filled curve shape at the user's
            score so only the area below/left of it reads as "shaded" —
            simple horizontal clip, not a re-derivation of the curve. */}
        <clipPath id={`iqCurveShadeClip-${variant}`}>
          <rect x="0" y="0" width={markerX} height={size.viewBoxHeight} />
        </clipPath>
      </defs>
      <g transform={`translate(0, ${size.translateY})`}>
        <path d={BELL_CURVE_FILL} fill="#2B2420" fillOpacity="0.05" />
        <path d={BELL_CURVE_FILL} fill="#FF8C6B" fillOpacity="0.3" clipPath={`url(#iqCurveShadeClip-${variant})`} />
        {/* 95% confidence band — only when the source actually supplies one
            (DEV FIXTURE scenarios today; real result never does). Never
            derived here, just plotted at the two already-supplied bounds. */}
        {hasCi && (
          <rect x={ciX1} y={36} width={ciX2 - ciX1} height={244} fill="#FF8C6B" fillOpacity="0.13" rx="6" />
        )}
        <path d={BELL_CURVE_OUTLINE} fill="none" stroke="#2B2420" strokeOpacity="0.35" strokeWidth={size.curveStrokeWidth} />

        <line x1="70" y1="280" x2="830" y2="280" stroke="#2B2420" strokeOpacity="0.15" strokeWidth={size.baselineStrokeWidth} />

        {BELL_CURVE_TICKS.map((tick) => (
          <g key={tick.label}>
            <line
              x1={tick.x}
              y1="280"
              x2={tick.x}
              y2={size.tickLineY2}
              stroke="#2B2420"
              strokeOpacity="0.25"
              strokeWidth={size.baselineStrokeWidth}
            />
            <text
              x={tick.x}
              y={size.tickTextY}
              textAnchor="middle"
              fontFamily="Manrope, sans-serif"
              fontWeight="600"
              fontSize={size.tickFontSize}
              fill="#2B2420"
              fillOpacity="0.5"
            >
              {tick.label}
            </text>
          </g>
        ))}

        <line
          x1="450.0"
          y1="280"
          x2="450.0"
          y2="70.0"
          stroke="#2B2420"
          strokeOpacity="0.3"
          strokeWidth={size.avgStrokeWidth}
          strokeDasharray="5 5"
        />
        <text
          x="450.0"
          y={size.avgTextY}
          textAnchor="middle"
          fontFamily="Sora, sans-serif"
          fontWeight="700"
          fontSize={size.avgFontSize}
          fill="#2B2420"
          fillOpacity="0.45"
        >
          Average · 100
        </text>

        <line x1={markerX} y1="280" x2={markerX} y2={markerY} stroke="#FF8C6B" strokeWidth={size.markerLineStrokeWidth} />
        <circle cx={markerX} cy={markerY} r={size.markerRadius} fill="#FF8C6B" stroke="#fff" strokeWidth={size.markerCircleStrokeWidth} />

        <rect x={boxX} y={boxTopY} width={size.labelBoxWidth} height={size.labelBoxHeight} rx={size.labelBoxRx} fill="#2B2420" />
        {/* Pointer/tail: base sits flush against the box's bottom edge,
            tip sits `pointerGap` units above the marker circle — always
            targeting the true markerX (see pointerX above), not the
            box's own (possibly edge-clamped) center. */}
        <polygon
          points={`${pointerX - size.pointerBaseHalfWidth},${pointerBaseY} ${
            pointerX + size.pointerBaseHalfWidth
          },${pointerBaseY} ${pointerX},${pointerTipY}`}
          fill="#2B2420"
        />
        <text
          x={boxX + size.labelBoxWidth / 2}
          y={textY}
          textAnchor="middle"
          fontFamily="Sora, sans-serif"
          fontWeight="700"
          fontSize={size.labelFontSize}
          fill="#FFF7F2"
        >
          You · {index}
          {percentile != null ? ` · ${formatPercentileMarkerLabel(percentile)}` : ""}
        </text>
      </g>
    </svg>
    );
  };

  return (
    <div className="w-full">
      {renderChart("mobile", CHART_SIZES.mobile, "block md:hidden")}
      {renderChart("desktop", CHART_SIZES.desktop, "hidden md:block")}
    </div>
  );
}

// Thresholds/labels come from PSYCHOMETRIC_CLASSIFICATION_BANDS — a
// different vocabulary/scale from the 5-key percentage bands shown
// elsewhere in this report; never conflate the two.
function ClassificationBandStrip({ index }) {
  const activeBand = PSYCHOMETRIC_CLASSIFICATION_BANDS.find((band) => index >= band.min && index <= band.max);
  return (
    <>
      <div className="mt-6 grid grid-cols-[repeat(auto-fit,minmax(92px,1fr))] gap-px bg-neutral-100 rounded-xl overflow-hidden border border-neutral-100">
        {PSYCHOMETRIC_CLASSIFICATION_BANDS.map((band) => {
          const isOn = band === activeBand;
          const range = Number.isFinite(band.min)
            ? Number.isFinite(band.max)
              ? `${band.min}–${band.max}`
              : `${band.min}+`
            : `≤${band.max}`;
          return (
            <div
              key={band.key}
              className={`px-2 py-2.5 text-center text-[0.68rem] font-bold leading-tight ${
                isOn ? "bg-[#2B2420] text-[rgba(255,247,242,0.75)]" : "bg-[#F9F6F4] text-neutral-500"
              }`}
            >
              <b className={`block text-[0.7rem] ${isOn ? "text-[#FFF7F2]" : "text-neutral-700"}`}>
                {band.label}
              </b>
              {/* font-normal overrides the cell's own font-bold: a bare <b>
                  inside a bold ancestor renders browser-"bolder" (900),
                  leaving almost no visual gap between the classification
                  label and this supporting range — this keeps the range
                  clearly secondary. */}
              <span className="font-normal">{range}</span>
            </div>
          );
        })}
      </div>
      <p className="text-[0.82rem] text-[#8A7F76] mt-3">
        Descriptive classifications follow the Wechsler convention used in the WISC-V and WAIS-5 manuals.
      </p>
    </>
  );
}

/* ============================================================
   DEEP-DIVE ILLUSTRATIONS
   ============================================================ */

// Shared CDN asset (not a localized /content/ asset) — same cdnUrl()/
// cdnSign() convention every other non-content image helper in this repo
// already uses (see utility-functions.js's getBannerImage etc.). Kept
// local to this file rather than added to utility-functions.js: it's a
// single fixed folder for exactly these 4 Deep Dive banners, not a
// general-purpose image concern other reports need.
const IQ_BANNER_BASE_PATH = "/assets/images/iq-banner";
const getIQBannerImage = (filename) => `${cdnUrl()}${IQ_BANNER_BASE_PATH}/${filename}${cdnSign()}`;

// domain.key -> the hosted SVG banner replacing that domain's inline
// illustration. Filenames are the exact, already-uploaded CDN asset names
// — not derived/lowercased/reformatted from the key.
const DOMAIN_BANNER_FILENAMES = {
  pattern_recognition: "section-1-pattern-recognition.svg",
  verbal_reasoning: "section-2-verbal-reasoning.svg",
  numerical_reasoning: "section-3-numerical-reasoning.svg",
  logical_deduction: "section-4-logical-deduction.svg",
};

// Hosted SVG renders first; the existing hand-drawn inline SVG (`children`)
// is preserved as fallback content only — rendered if the hosted image
// fails to load (e.g. CDN hiccup), never removed from the codebase.
function IllustrationFrame({ background, imgSrc, alt, children }) {
  const [imgFailed, setImgFailed] = useState(false);
  return (
    <div
      className="w-full rounded-[20px] overflow-hidden"
      style={{ aspectRatio: "4 / 3", background, boxShadow: "rgba(43, 36, 32, 0.05) 0px 0px 0px 1px inset" }}
    >
      {imgSrc && !imgFailed ? (
        <img src={imgSrc} alt={alt} className="w-full h-full object-contain" onError={() => setImgFailed(true)} />
      ) : (
        <svg viewBox="0 0 400 300" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
          {children}
        </svg>
      )}
    </div>
  );
}

function MatrixCell({ x, y, fillOpacity, radius, questionMark }) {
  return (
    <g>
      <rect x={x} y={y} width="58" height="58" rx="13" fill={questionMark ? "#ffffff" : "#8ECAE6"} fillOpacity={questionMark ? 1 : fillOpacity} stroke="#2B2420" strokeOpacity="0.14" />
      {questionMark ? (
        <text x={x + 29} y={y + 40} textAnchor="middle" fontFamily="Sora, sans-serif" fontWeight="800" fontSize="32" fill="#8ECAE6">?</text>
      ) : (
        <circle cx={x + 29} cy={y + 29} r={radius} fill="#2B2420" fillOpacity="0.6" />
      )}
    </g>
  );
}

function MatrixIllustration({ imgSrc, alt }) {
  const cells = [
    { x: 100, y: 48, fillOpacity: 0.32, radius: 6 },
    { x: 174, y: 48, fillOpacity: 0.38, radius: 8.1 },
    { x: 248, y: 48, fillOpacity: 0.44, radius: 10.2 },
    { x: 100, y: 122, fillOpacity: 0.5, radius: 12.3 },
    { x: 174, y: 122, fillOpacity: 0.56, radius: 14.4 },
    { x: 248, y: 122, fillOpacity: 0.62, radius: 16.5 },
    { x: 100, y: 196, fillOpacity: 0.68, radius: 18.6 },
    { x: 174, y: 196, fillOpacity: 0.74, radius: 20.7 },
    { x: 248, y: 196, questionMark: true },
  ];
  return (
    <IllustrationFrame background="rgba(142, 202, 230, 0.15)" imgSrc={imgSrc} alt={alt}>
      {cells.map((cell, i) => (
        <MatrixCell key={i} {...cell} />
      ))}
    </IllustrationFrame>
  );
}

function AnalogyBox({ x, letter, isAnswer }) {
  return (
    <g>
      <rect x={x} y="116" width="58" height="58" rx="14" fill={isAnswer ? "#ffffff" : "#C9A7FF"} fillOpacity={isAnswer ? 1 : 0.9} stroke="#2B2420" strokeOpacity="0.14" />
      <text x={x + 29} y="156" textAnchor="middle" fontFamily="Sora, sans-serif" fontWeight="800" fontSize="30" fill="#2B2420" fillOpacity="0.82">{letter}</text>
    </g>
  );
}

function AnalogyColon({ x }) {
  return (
    <g>
      <circle cx={x} cy="138" r="4.5" fill="#2B2420" fillOpacity="0.4" />
      <circle cx={x} cy="156" r="4.5" fill="#2B2420" fillOpacity="0.4" />
    </g>
  );
}

function AnalogyIllustration({ imgSrc, alt }) {
  return (
    <IllustrationFrame background="rgba(201, 167, 255, 0.15)" imgSrc={imgSrc} alt={alt}>
      <AnalogyBox x={24} letter="A" />
      <AnalogyColon x={102} />
      <AnalogyBox x={116} letter="B" />
      <AnalogyColon x={200} />
      <AnalogyColon x={214} />
      <AnalogyBox x={232} letter="C" />
      <AnalogyColon x={310} />
      <AnalogyBox x={322} letter="?" isAnswer />
    </IllustrationFrame>
  );
}

function DataPoint({ x, y, label }) {
  return (
    <g>
      <circle cx={x} cy={y} r="7" fill="#ffffff" stroke="#2B2420" strokeWidth="3" strokeOpacity="0.7" />
      <text x={x} y={y - 16} textAnchor="middle" fontFamily="Sora, sans-serif" fontWeight="700" fontSize="17" fill="#2B2420" fillOpacity="0.65">{label}</text>
    </g>
  );
}

function GrowthCurveIllustration({ imgSrc, alt }) {
  const points = [
    { x: 52, y: 244.9, label: "2" },
    { x: 126, y: 218.8, label: "6" },
    { x: 200, y: 179.6, label: "12" },
    { x: 274, y: 127.3, label: "20" },
    { x: 348, y: 62.0, label: "30" },
  ];
  const linePoints = points.map((p) => `${p.x},${p.y}`).join(" ");
  const areaPath = `M52,258 L${points.map((p) => `${p.x},${p.y}`).join(" L")} L348,258 Z`;

  return (
    <IllustrationFrame background="rgba(255, 140, 107, 0.15)" imgSrc={imgSrc} alt={alt}>
      <line x1="40" y1="258" x2="360" y2="258" stroke="#2B2420" strokeOpacity="0.12" strokeWidth="2" />
      <path d={areaPath} fill="#FF8C6B" fillOpacity="0.2" />
      <polyline points={linePoints} fill="none" stroke="#FF8C6B" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p) => (
        <DataPoint key={p.label} {...p} />
      ))}
    </IllustrationFrame>
  );
}

function SyllogismIllustration({ imgSrc, alt }) {
  return (
    <IllustrationFrame background="rgba(255, 209, 102, 0.15)" imgSrc={imgSrc} alt={alt}>
      <circle cx={166} cy="118" r="70" fill="#FFD166" fillOpacity={0.34} stroke="#2B2420" strokeOpacity="0.1" />
      <circle cx={234} cy="118" r="70" fill="#FFD166" fillOpacity={0.55} stroke="#2B2420" strokeOpacity="0.1" />
      <text x={130} y="128" textAnchor="middle" fontFamily="Sora, sans-serif" fontWeight="800" fontSize="30" fill="#2B2420" fillOpacity="0.7">P</text>
      <text x={270} y="128" textAnchor="middle" fontFamily="Sora, sans-serif" fontWeight="800" fontSize="30" fill="#2B2420" fillOpacity="0.7">Q</text>
      <line x1="200" y1="192" x2="200" y2="228" stroke="#2B2420" strokeOpacity="0.35" strokeWidth="3.5" />
      <path d="M200 232 l-6 -13 l12 0 z" fill="#2B2420" fillOpacity="0.35" />
      <rect x="150" y="232" width="100" height="44" rx="22" fill="#2B2420" />
      <text x="200" y="262" textAnchor="middle" fontFamily="Sora, sans-serif" fontWeight="800" fontSize="26" fill="#FFF7F2">∴</text>
    </IllustrationFrame>
  );
}

const DOMAIN_ILLUSTRATIONS = {
  pattern_recognition: MatrixIllustration,
  verbal_reasoning: AnalogyIllustration,
  numerical_reasoning: GrowthCurveIllustration,
  logical_deduction: SyllogismIllustration,
};

/* ============================================================
   DEEP-DIVE SECTION (shared layout for all 4 domains)
   ============================================================ */

function StepMarker({ number, color }) {
  return (
    <div className="flex items-center gap-4 mb-5">
      <span className={`text-3xl font-extrabold ${color.text}`}>{number}</span>
      <span className={`h-0.5 w-14 ${color.bg}`} />
    </div>
  );
}

function SectionTitle({ children }) {
  // break-words is defensive, not decorative: a domain title falling back
  // to its raw machine key (e.g. content lookup failing for one domain)
  // is a single long unbreakable word that would otherwise force this grid
  // column wider than its viewport — see the mobile-overflow investigation
  // notes. Normal (spaced) titles are unaffected.
  return <h2 className="text-4xl md:text-5xl font-extrabold text-neutral-900 leading-[1.05] mb-5 break-words">{children}</h2>;
}

// Three-tier responsive layout, decoupled into two independent
// breakpoints (2026-09-16 — this project's own `md` token is 821px, see
// tailwind.config.js):
//   < md (821px): single column, stacked, illustration full-width.
//   md-1151px: side-by-side, but an ASYMMETRIC template — text gets the
//     remaining space, illustration is capped at a fixed 260px — so the
//     illustration can't balloon to fill half a wide row. InsightRow
//     stays stacked here (see its own comment): at this width the text
//     column alone isn't yet wide enough for two comfortable cards.
//   >= 1152px: the original equal-width 2-column desktop layout,
//     unchanged. InsightRow also goes side-by-side here, once the text
//     column reaches its full (undivided) share of the row.
// Both breakpoints must move together with InsightRow's if ever revisited
// — they describe one coupled layout.
function DeepDiveSection({ domain, index }) {
  const style = getDomainVisualStyle(domain.key);
  const Illustration = DOMAIN_ILLUSTRATIONS[domain.key];
  const bannerFilename = DOMAIN_BANNER_FILENAMES[domain.key];
  const bannerImgSrc = bannerFilename ? getIQBannerImage(bannerFilename) : null;
  const illustrationAlt = domain.title || domain.key;
  // Alternates which side each domain's illustration sits on via CSS
  // `order` (not DOM order, so the mobile stack always reads
  // heading-then-illustration).
  const textFirstOnDesktop = index % 2 === 0;

  const textColumn = (
    <div className={textFirstOnDesktop ? "md:order-1" : "md:order-2"}>
      <StepMarker number={String(index + 1).padStart(2, "0")} color={style} />
      <SectionTitle>{domain.title || domain.key}</SectionTitle>
      {domain.subtitle && <p className="text-[0.8rem] font-semibold text-neutral-400 mb-5">{domain.subtitle}</p>}
      {domain.overview && <Description className="mb-5">{domain.overview}</Description>}
      <ScoreBar
        label="Section score"
        percentage={domain.percentage}
        barColor={style.bg}
        classificationLabel={domain.classificationLabel}
      />
      <InsightRow strength={domain.strength} improve={domain.improve} />
    </div>
  );

  const illustrationColumn = Illustration ? (
    <div className={`self-start md:mt-14 ${textFirstOnDesktop ? "md:order-2" : "md:order-1"}`}>
      <Illustration imgSrc={bannerImgSrc} alt={illustrationAlt} />
    </div>
  ) : null;

  // `order` alone only changes visual position, not which track's WIDTH
  // an item lands in — with a fixed [1fr_260px] template, an order-swapped
  // illustration would land in the wide 1fr track instead of the 260px
  // one. So the md-tier template itself mirrors when the illustration is
  // first: [260px_1fr] rather than [1fr_260px]. At 1152px+ both tracks are
  // equal (grid-cols-2), so order-swapping alone is safe there.
  const midTemplate = textFirstOnDesktop
    ? "md:grid-cols-[minmax(0,1fr)_260px]"
    : "md:grid-cols-[260px_minmax(0,1fr)]";

  return (
    <div
      className={`grid grid-cols-1 ${midTemplate} md:gap-x-8 min-[1152px]:grid-cols-2 min-[1152px]:gap-x-16 gap-y-8 items-start`}
      data-visual-section={domain.key}
    >
      {textColumn}
      {illustrationColumn}
    </div>
  );
}

/* ============================================================
   RETAKE / SHARE CTA
   ============================================================ */

function CtaTitle({ children }) {
  return (
    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-neutral-900 leading-tight text-center max-w-2xl mx-auto mb-4 sm:mb-5 lg:mb-6">
      {children}
    </h2>
  );
}

function CtaDescription({ children }) {
  return (
    <p className="text-base sm:text-lg lg:text-xl text-neutral-600 text-center max-w-lg mx-auto mb-7 sm:mb-8 lg:mb-9">
      {children}
    </p>
  );
}

function PrimaryButton({ children, onClick }) {
  return (
    <button onClick={onClick} className="bg-neutral-900 text-white font-bold text-lg px-7 py-4 rounded-full hover:bg-neutral-800 transition-colors">
      {children}
    </button>
  );
}

function ButtonRow({ children }) {
  return <div className="flex flex-wrap items-center justify-center gap-4">{children}</div>;
}

function RetakeCta({ onRetake, isAuth, shareProps }) {
  return (
    <div
      className="relative w-full max-w-6xl mx-auto bg-sky-300 rounded-[28px] overflow-hidden px-6 py-12 sm:px-8 sm:py-14 md:px-16 md:py-16"
      data-visual-section="cta"
    >
      <div className="absolute -top-16 -right-10 w-72 h-72 rounded-full bg-sky-200/50" />
      <div className="relative">
        <CtaTitle>Want to push your score higher?</CtaTitle>
        <CtaDescription>Retake the assessment, or share how you did with friends.</CtaDescription>
        <ButtonRow>
          <PrimaryButton onClick={onRetake}>Retake the test →</PrimaryButton>
          {isAuth && (
            <div className="flex flex-wrap items-center justify-center gap-3">
              <ShareButton {...shareProps.share} variant="pill" />
              <SendToFriendButton {...shareProps.sendToFriend} variant="pill" />
            </div>
          )}
        </ButtonRow>
      </div>
    </div>
  );
}

/* ============================================================
   PAGE
   ============================================================ */

const IQReport = ({
  funnelStep,
  getUserByIdConnect,
  getInsightfulContentConnect,
  getAssessmentResultByAssessmentAttemptIdConnect,
  sendToFriendReportOnEmailConnect,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { userGUID, isAuth } = funnelStep || {};
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const userId = queryParams.get("uid") || userGUID;
  const assessmentAttemptId = queryParams.get("aai");

  const [userData, setUserData] = useState(null);
  const [insightfulContent, setInsightfulContent] = useState(null);
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [isContentLoading, setIsContentLoading] = useState(true);
  const [isResultLoading, setIsResultLoading] = useState(true);
  const isLoading = isUserLoading || isContentLoading || isResultLoading;
  const [devOverrides, setDevOverrides] = useState(EMPTY_DEV_OVERRIDES);
  const devPreviewAllowed = isDevPreviewAllowed(queryParams);

  useEffect(() => {
    if (userId && isAuth) {
      setIsUserLoading(true);
      getUserByIdConnect(userId)
        .then((res) => {
          if (res) setUserData(res);
        })
        .finally(() => setIsUserLoading(false));
    }
  }, [userId, isAuth]);

  // Real content fetch — IQTypeModel/ALL_CONTENT, never mocked. Keyed on
  // currentLocale (route-authoritative, from useParams()).
  useEffect(() => {
    getInsightfulContentConnect({
      modelType: MODEL_TYPE_IQ[currentLocale] || MODEL_TYPE_IQ[LOCALE.EN],
      contentKey: IQ_CONTENT_KEY,
    })
      .then((res) => {
        if (res) setInsightfulContent(res);
      })
      .catch((error) => console.error("Error fetching IQ content:", error))
      .finally(() => setIsContentLoading(false));
  }, [currentLocale]);

  // Real result fetch. IQ_TEST_ASSESSMENT_ID's /result endpoint is not yet
  // confirmed to return real data — any failure/empty response is always
  // logged, regardless of environment; only non-production environments may
  // then fall back to mock-data.js (see assessmentResultData below).
  useEffect(() => {
    if (!assessmentAttemptId) {
      setIsResultLoading(false);
      return;
    }
    getAssessmentResultByAssessmentAttemptIdConnect(assessmentAttemptId, currentLocale)
      .then((res) => {
        if (res) setAssessmentResult(res);
        else console.error("IQ report: no assessment result returned for", assessmentAttemptId);
      })
      .catch((error) => console.error("IQ report: assessment result fetch failed", error))
      .finally(() => setIsResultLoading(false));
  }, [assessmentAttemptId, currentLocale]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  const assessmentResultData = assessmentResult || (isNonProdEnv && MOCK_ASSESSMENT_RESULT);

  // Scenario percentages are injected onto the raw result strictly BEFORE
  // mapIQReportModel runs, so a DevTools preset exercises the exact same
  // calculation path real backend percentages do. Never applied outside
  // devPreviewAllowed.
  const scenarioAssessmentResult =
    devPreviewAllowed && assessmentResultData
      ? applyDevSourceInputs(assessmentResultData, devOverrides.domainPercentages)
      : assessmentResultData;

  const reportData =
    insightfulContent && scenarioAssessmentResult
      ? mapIQReportModel(scenarioAssessmentResult, insightfulContent)
      : null;

  const handleDevDomainChange = (key, percentage) =>
    setDevOverrides((prev) => ({
      ...prev,
      domainPercentages: { ...prev.domainPercentages, [key]: percentage },
    }));

  const handleDevPreset = (name) => {
    const preset = NAMED_DEV_PRESETS[name];
    setDevOverrides({ domainPercentages: preset.domainPercentages });
  };

  const handleDevReset = () => setDevOverrides(EMPTY_DEV_OVERRIDES);

  const handleDevLocaleChange = (targetLocale) => {
    const targetPath = PathHelper(APP_NAVIGATION.IQ_TEST_REPORT, targetLocale).next;
    window.location.assign(`${targetPath}${location.search}`);
  };

  const handleRetake = () => {
    const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);
    navigate(`${quizPath.next}?assessmentId=${IQ_TEST_ASSESSMENT_ID}&newt=true`);
  };

  const shareProps = {
    share: {
      currentAssessmentAttemptData: assessmentResultData,
      userGUID,
      userData,
      label: t("dashboard.share_report"),
    },
    sendToFriend: {
      currentAssessmentAttemptData: assessmentResultData,
      userGUID,
      userData,
      CreateGuestUserConnect: sendToFriendReportOnEmailConnect,
      label: t("dashboard.send_to_friend"),
    },
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <div className="flex justify-center items-center h-screen w-full bg-[#FFF7F2]">
          <Loader isLoading={isLoading} />
        </div>
        <Footer />
      </>
    );
  }

  if (!reportData) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center px-8 bg-[#FFF7F2]">
          <div className="max-w-md text-center">
            <p className="text-2xl font-extrabold text-neutral-900 mb-3">Report not available yet</p>
            <p className="text-neutral-500 leading-relaxed">
              We couldn't load your IQ report right now. Please check back soon.
            </p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      {/* Section-to-section rhythm: 24px mobile / 28px tablet (sm) / 32px
          desktop (md) — a small, predictable scale (both the outer
          top/bottom padding and the gap between major sections use the
          same values) rather than the previous 56/64/80px space-y, which
          read as excessive dead space between sections, especially on
          narrow screens where every section is stacked in one long
          column. Mobile gutters (px-4) and the cream canvas are
          unchanged; card-internal padding/spacing is untouched. */}
      <div className="min-h-screen bg-[#FFF7F2] px-4 sm:px-6 md:px-16 py-6 sm:py-7 md:py-8 space-y-6 sm:space-y-7 md:space-y-8">
        {/* Header/Intro + Hero + Section Scores — always shown, no
            visibility config. */}
        <div className="max-w-6xl mx-auto" data-visual-section="overview">
          <HeroKicker>{reportData.header.subtitle || "Your Results"}</HeroKicker>
          <Title>{reportData.header.title || "Cognitive Assessment Report"}</Title>
          {/* Band-dependent classification description, shown once here
              (never repeated inside IQCard below, whose primary number is
              the separate IQ/deviation index) — the caption exists only
              so the IQ badge below doesn't read as describing this text. */}
          {(reportData.overallResult.classificationDescription || reportData.intro.body) && (
            <>
              <p className="text-xs font-bold uppercase tracking-wide text-neutral-400 mb-2">
                Your performance summary
              </p>
              <Subtitle>
                {reportData.overallResult.classificationDescription || reportData.intro.body}
              </Subtitle>
            </>
          )}

          {/* Design-parity experiment: reproduces the reference's own
              structural idea literally — display:flex + flex-wrap, each
              card given its own flex-basis (Hero 300px, Section Scores
              380px), fluid gap/margin-top via clamp(). No fixed pixel
              breakpoint: intrinsic flex-wrap decides when the two cards
              stack, exactly as the reference does. */}
          <div
            className={reportData.overallResult.percentage != null ? "flex flex-wrap items-stretch" : ""}
            style={
              reportData.overallResult.percentage != null
                ? { gap: "clamp(18px, 2.5vw, 26px)", marginTop: "clamp(24px, 3vw, 36px)" }
                : undefined
            }
          >
            {reportData.overallResult.percentage != null && (
              <IQCard overallResult={reportData.overallResult} />
            )}
            <DomainScoresCard domains={reportData.domains} />
          </div>
        </div>

        {reportData.overallResult.psychometric?.estimatedIq != null && (
          <div className="max-w-6xl mx-auto bg-white rounded-3xl shadow-sm px-5 py-8 md:p-12" data-visual-section="where-you-rank">
            <CardHeader
              eyebrow="How you compare"
              title="Where you rank"
              description="Your score sits relative to the average. The shaded region is the share of test-takers you scored above."
            />
            <BellCurveChart
              index={reportData.overallResult.psychometric.estimatedIq}
              percentile={reportData.overallResult.psychometric.percentile}
            />
            <Legend />
            <ClassificationBandStrip index={reportData.overallResult.psychometric.estimatedIq} />
          </div>
        )}

        {/* Deep dive */}
        <div className="max-w-6xl mx-auto">
          <Eyebrow>Deep dive</Eyebrow>
          <h1 className="text-5xl font-extrabold text-neutral-900 leading-tight mb-5 break-words">
            {reportData.detailedBreakdownIntro?.heading || "How you did, domain by domain"}
          </h1>
          {reportData.detailedBreakdownIntro?.body && (
            <Description className="mb-14 max-w-3xl">{reportData.detailedBreakdownIntro.body}</Description>
          )}
          <div className="space-y-12">
            {reportData.domains.map((domain, index) => (
              <DeepDiveSection key={domain.key} domain={domain} index={index} />
            ))}
          </div>
        </div>

        {/* CTA */}
        <RetakeCta onRetake={handleRetake} isAuth={isAuth} shareProps={shareProps} />

        <ReportWrapper assessmentId={IQ_TEST_ASSESSMENT_ID} assessmentAttemptId={assessmentAttemptId} currentLocale={currentLocale} />
      </div>
      <Footer />

      {devPreviewAllowed && reportData && (
        <ReportDebugPanel
          overrides={devOverrides}
          onDomainChange={handleDevDomainChange}
          onPreset={handleDevPreset}
          onReset={handleDevReset}
          currentLocale={currentLocale}
          onLocaleChange={handleDevLocaleChange}
        />
      )}
    </>
  );
};

export default IQReport;
